import React, { useState, useEffect } from 'react';

const JoinUsSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'contact' | 'volunteer'>('contact');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [volunteerData, setVolunteerData] = useState({
    name: '',
    email: '',
    interests: [] as string[],
    otherInterest: '',
    skills: ''
  });

  useEffect(() => {
    const handleTabChange = (event: CustomEvent) => {
        if (event.detail === 'volunteer' || event.detail === 'contact') {
            setActiveTab(event.detail);
        }
    };

    window.addEventListener('setActiveJoinUsTab', handleTabChange as EventListener);

    return () => {
        window.removeEventListener('setActiveJoinUsTab', handleTabChange as EventListener);
    };
  }, []);

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVolunteerChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setVolunteerData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleInterestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setVolunteerData(prev => {
      const newInterests = checked 
        ? [...prev.interests, value]
        : prev.interests.filter(interest => interest !== value);

      // If "Others" is unchecked, clear the custom interest text
      const newOtherInterest = (value === 'Others' && !checked) ? '' : prev.otherInterest;

      return { ...prev, interests: newInterests, otherInterest: newOtherInterest };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeTab === 'contact') {
      console.log('Contact Form submitted:', formData);
      setFormData({ name: '', email: '', message: '' });
    } else {
      console.log('Volunteer Form submitted:', volunteerData);
      setVolunteerData({ name: '', email: '', interests: [], otherInterest: '', skills: '' });
    }
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000); // Reset after 5 seconds
  };
  
  const interestOptions = [
    "Tech (Web/App Development)",
    "Animation & Video Production",
    "Course Design & Content",
    "Graphic Design & Comics",
  ];

  return (
    <section id="joinus" className="py-20 bg-jskool-cream">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-heading font-extrabold text-jskool-dark">Get Involved</h2>
          <p className="mt-4 text-lg text-jskool-dark/70 max-w-3xl mx-auto">
            Whether you have a question or want to contribute your skills, we'd love to hear from you.
          </p>
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-4 md:p-8">
          <div className="flex justify-center border-b mb-6">
            <button 
              onClick={() => setActiveTab('contact')}
              className={`font-heading font-bold text-lg py-3 px-6 transition-colors duration-300 rounded-t-lg ${activeTab === 'contact' ? 'bg-jskool-blue text-white' : 'text-jskool-dark/60 hover:bg-gray-100'}`}
            >
              Contact Us
            </button>
            <button 
              onClick={() => setActiveTab('volunteer')}
              className={`font-heading font-bold text-lg py-3 px-6 transition-colors duration-300 rounded-t-lg ${activeTab === 'volunteer' ? 'bg-jskool-blue text-white' : 'text-jskool-dark/60 hover:bg-gray-100'}`}
            >
              Volunteer
            </button>
          </div>

          {isSubmitted ? (
            <div className="text-center py-12">
              <h3 className="text-2xl font-bold font-heading text-jskool-green">Thank you!</h3>
              <p className="mt-2 text-lg text-jskool-dark/80">Your submission has been received. We'll be in touch soon!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-4 md:px-8 pb-4">
              {activeTab === 'contact' && (
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="font-semibold text-jskool-dark/80 mb-2 block">Full Name</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleContactChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jskool-blue" />
                  </div>
                  <div>
                    <label htmlFor="email" className="font-semibold text-jskool-dark/80 mb-2 block">Email Address</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleContactChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jskool-blue" />
                  </div>
                  <div>
                    <label htmlFor="message" className="font-semibold text-jskool-dark/80 mb-2 block">Message</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleContactChange} rows={5} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jskool-blue"></textarea>
                  </div>
                  <div className="text-center pt-2">
                    <button type="submit" className="bg-jskool-blue hover:bg-blue-700 text-white font-bold font-heading py-3 px-12 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300">
                      Send Message
                    </button>
                  </div>
                </div>
              )}
              
              {activeTab === 'volunteer' && (
                <div className="space-y-6">
                  <div>
                    <label htmlFor="volunteer-name" className="font-semibold text-jskool-dark/80 mb-2 block">Full Name</label>
                    <input type="text" id="volunteer-name" name="name" value={volunteerData.name} onChange={handleVolunteerChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jskool-gold" />
                  </div>
                  <div>
                    <label htmlFor="volunteer-email" className="font-semibold text-jskool-dark/80 mb-2 block">Email Address</label>
                    <input type="email" id="volunteer-email" name="email" value={volunteerData.email} onChange={handleVolunteerChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jskool-gold" />
                  </div>
                  <div>
                    <label className="font-semibold text-jskool-dark/80 mb-2 block">Area of Interest</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      {interestOptions.map(interest => (
                         <label key={interest} className="flex items-center space-x-3">
                           <input type="checkbox" value={interest} checked={volunteerData.interests.includes(interest)} onChange={handleInterestChange} className="h-5 w-5 rounded border-gray-300 text-jskool-gold focus:ring-jskool-gold" />
                           <span>{interest}</span>
                         </label>
                      ))}
                       <label key="Others" className="flex items-center space-x-3">
                         <input type="checkbox" value="Others" checked={volunteerData.interests.includes('Others')} onChange={handleInterestChange} className="h-5 w-5 rounded border-gray-300 text-jskool-gold focus:ring-jskool-gold" />
                         <span>Others</span>
                       </label>
                    </div>
                    {volunteerData.interests.includes('Others') && (
                      <div className="mt-4">
                        <label htmlFor="other-interest" className="font-semibold text-jskool-dark/80 mb-2 block sr-only">Please specify other interest</label>
                        <input
                          type="text"
                          id="other-interest"
                          name="otherInterest"
                          value={volunteerData.otherInterest}
                          onChange={handleVolunteerChange}
                          placeholder="Please specify..."
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jskool-gold"
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label htmlFor="skills" className="font-semibold text-jskool-dark/80 mb-2 block">Briefly describe your skills</label>
                    <textarea id="skills" name="skills" value={volunteerData.skills} onChange={handleVolunteerChange} rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jskool-gold"></textarea>
                  </div>
                  <div className="text-center pt-2">
                    <button type="submit" className="bg-jskool-gold hover:bg-yellow-500 text-jskool-dark font-bold font-heading py-3 px-12 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300">
                      Register as Volunteer
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default JoinUsSection;